import type { Account, ImportSource } from '@keel/core';
import { BRAND } from '@/brand';

/** Guided statement import for banks without open banking: `/transactions/import?guide=amex|bank[&account=…]`. */
export interface ImportGuide {
  readonly id: 'amex' | 'bank';
  readonly title: string;
  readonly intro: string;
  readonly steps: readonly string[];
  /** Used when detection doesn't recognise the file; `forcePreset` also skips column matching for it. */
  readonly preset: string;
  readonly forcePreset?: boolean;
  readonly presetName: string;
  /** Marks the account statement-fed on commit (account-page line + monthly reminder). */
  readonly source: ImportSource;
  /** Offered (and preselected) when no matching account exists yet. */
  readonly newAccount?: { readonly name: string; readonly type: Account['type']; readonly institution: string };
  readonly matches: (a: Account) => boolean;
}

export const IMPORT_GUIDES: Record<ImportGuide['id'], ImportGuide> = {
  amex: {
    id: 'amex',
    title: 'Connect American Express',
    intro: `Amex isn’t part of open banking yet, so connect by importing your statement — it takes a minute and ${BRAND.name} skips anything already imported.`,
    steps: [
      'On americanexpress.com.au, go to Statements & Activity → Custom Date Range, pick the dates (first time: as far back as you like), then Download → CSV.',
      'Drop the file here.',
      'Statements older than about two years are PDFs only — drop the PDFs in too (one after another; overlaps are skipped).',
    ],
    preset: 'amex', forcePreset: true, presetName: 'American Express (AU)', source: 'amex_csv',
    newAccount: { name: 'American Express', type: 'credit_card', institution: 'American Express' },
    matches: (a) => a.importSource === 'amex_csv' || (a.type === 'credit_card' && /amex|american express/i.test(`${a.institution ?? ''} ${a.name}`)),
  },
  bank: {
    id: 'bank',
    title: 'Connect a bank that isn’t listed',
    intro: `If your bank isn’t available through open banking, connect by importing its statement — it takes a minute and ${BRAND.name} skips anything already imported.`,
    steps: ['In your bank’s app or website, download your transactions or statement as a CSV (OFX or QIF also work).', 'Drop the file here.'],
    preset: 'generic_bank', presetName: 'Generic bank CSV', source: 'manual_statement',
    matches: (a) => a.importSource === 'manual_statement',
  },
};

export const guideFor = (id: string | null): ImportGuide | undefined => (id === 'amex' || id === 'bank' ? IMPORT_GUIDES[id] : undefined);
export const guideHref = (id: ImportGuide['id'], accountId?: string): string =>
  `/transactions/import?guide=${id}${accountId ? `&account=${encodeURIComponent(accountId)}` : ''}`;
